import React from 'react'
import { Trans, t } from '@lingui/macro'

import NewActionHeader from '../new-action-header/NewActionHeader'
import { ButtonPrimary, ButtonLight, Flex } from 'front-end/src/components'

import { ICampaignBannerComponentState } from './CampaignBannerComponent'
import IconCalendar from '../../../../components/common/icon/IconCalendar'
import IconArrowLeft from '../../../../components/common/icon/IconArrowLeft'
import ActionScheduleChoice from '../action-schedule-choice/ActionScheduleChoice'
import DateTimePickers from '../../../../components/common/date-time-pickers/DateTimePickers'
import SelectTimezone from '../../../../components/common/SelectTimezone'
import { CampaignActionEditingSteps } from '../../common'
import { CampaignScheduleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import dayjs from 'shared/dayjs'
import { RelativeDayTimePicker } from './RelativeDayTimePicker'

export const CampaignBannerStepSchedule = (
  props: ICampaignBannerComponentState & {
    onStateUpdate: (
      updatedState: Partial<ICampaignBannerComponentState>
    ) => void
    onSave: () => void
  }
) => {
  const { relativeSchedule } = props

  const isRelativeChoiceValid = () => {
    if (
      relativeSchedule.timeFrom &&
      typeof relativeSchedule.dayFrom === 'number' &&
      relativeSchedule.dayFrom >= 0 &&
      typeof relativeSchedule.dayTo === 'number' &&
      relativeSchedule.dayTo >= 0 &&
      relativeSchedule.timeTo
    ) {
      const timeFromSplit = relativeSchedule.timeFrom?.split(':')
      const timeToSplit = relativeSchedule.timeTo?.split(':')

      const fromDate = dayjs()
        .add(relativeSchedule.dayFrom, 'day')
        .startOf('day')
        .add(Number(timeFromSplit[0]), 'hour')
        .add(Number(timeFromSplit[1]), 'minute')

      const toDate = dayjs()
        .add(relativeSchedule.dayTo, 'day')
        .startOf('day')
        .add(Number(timeToSplit[0]), 'hour')
        .add(Number(timeToSplit[1]), 'minute')

      return fromDate.isBefore(toDate)
    }
    return false
  }

  const isRelativeScheduleSaving = (): boolean => {
    return (
      props.isSaving && props.scheduleType === CampaignScheduleEnum.RELATIVE
    )
  }

  const isFixedChoiceValid = (): boolean => {
    const { dateFrom, dateTo, valid } = props.fixedSchedule
    if (valid === false) {
      return false
    }
    if (!(dateFrom && dateTo)) {
      return false
    }
    if (!(dateFrom.isValid() && dateTo.isValid())) {
      return false
    }
    return dateFrom.isBefore(dateTo)
  }

  const isFixedScheduleSaving = (): boolean => {
    return props.isSaving && props.scheduleType === CampaignScheduleEnum.FIXED
  }

  return (
    <div className="CampaignBannerComponent CampaignBannerComponent__schedule">
      <NewActionHeader
        title={t`Show a banner`}
        steps={CampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.SCHEDULE}
        prevButton={
          <ButtonLight
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.PREVIEW
              })
            }}
            leftIcon={<IconArrowLeft />}
          >
            <Trans>Back</Trans>
          </ButtonLight>
        }
      />

      <div className="ActionSchedule">
        {props.scheduleType === CampaignScheduleEnum.RELATIVE && (
          <ActionScheduleChoice
            selected={props.scheduleType === CampaignScheduleEnum.RELATIVE}
          >
            <h2>
              <IconCalendar />
              <Trans>Relative schedule</Trans>
            </h2>
            <section>
              <Trans>
                Schedule your action to execute according to how long a person
                has been part of your campaign.
              </Trans>
            </section>
            <Flex alignItems="center" justifyContent="center" m="4px">
              <RelativeDayTimePicker
                setDay={(day) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...props.relativeSchedule,
                      dayFrom: day
                    }
                  })
                }}
                setTime={(time) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...relativeSchedule,
                      timeFrom: `${time}:00`
                    }
                  })
                }}
                day={relativeSchedule.dayFrom}
                time={relativeSchedule.timeFrom}
                prefixLabel={t`Show from day`}
              />
            </Flex>
            <Flex alignItems="center" justifyContent="center" m="4px" mb={5}>
              <RelativeDayTimePicker
                setDay={(day) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...props.relativeSchedule,
                      dayTo: day
                    }
                  })
                }}
                setTime={(time) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...relativeSchedule,
                      timeTo: `${time}:00`
                    }
                  })
                }}
                day={relativeSchedule.dayTo}
                time={relativeSchedule.timeTo}
                prefixLabel={t`Show to day`}
              />
            </Flex>
            <ButtonPrimary
              isFullWidth
              onClick={() => {
                props.onStateUpdate({
                  scheduleType: CampaignScheduleEnum.RELATIVE,
                  fixedSchedule: {
                    dateFrom: null,
                    dateTo: null
                  }
                })
                props.onSave()
              }}
              isLoading={props.isSaving}
              disabled={!isRelativeChoiceValid() || props.isSaving}
            >
              <Trans>Confirm schedule and save</Trans>
            </ButtonPrimary>
          </ActionScheduleChoice>
        )}

        {props.scheduleType === CampaignScheduleEnum.FIXED && (
          <ActionScheduleChoice
            selected={props.scheduleType === CampaignScheduleEnum.FIXED}
          >
            <h2>
              <IconCalendar />
              <Trans>Fixed schedule</Trans>
            </h2>
            <section>
              <Trans>
                Schedule your action to execute on a specific date, to everyone
                in your campaign audience.
              </Trans>
            </section>
            <section className="ActionScheduleChoice__set-date-input-container">
              <DateTimePickers
                datePlaceholder={t`Day`}
                timePlaceholder={t`Time`}
                label={t`Show from`}
                fullWidth
                variant="light"
                isUtc
                labelStyle={{ minWidth: '100%' }}
                disablePastDays
                value={props.fixedSchedule.dateFrom}
                onChange={(setDate) => {
                  const { fixedSchedule } = props
                  if (
                    setDate &&
                    setDate instanceof dayjs &&
                    setDate.isValid()
                  ) {
                    props.onStateUpdate({
                      fixedSchedule: {
                        ...fixedSchedule,
                        dateFrom: setDate,
                        valid: true
                      }
                    })
                  } else {
                    props.onStateUpdate({
                      fixedSchedule: {
                        ...fixedSchedule,
                        valid: false,
                        dateFrom: fixedSchedule.dateFrom
                      }
                    })
                  }
                }}
              />
            </section>
            <section className="ActionScheduleChoice__set-date-input-container">
              <DateTimePickers
                datePlaceholder={t`Day`}
                timePlaceholder={t`Time`}
                label={t`Show to`}
                variant="light"
                isUtc
                labelStyle={{ minWidth: '100%' }}
                disablePastDays
                value={props.fixedSchedule.dateTo}
                fullWidth
                onChange={(setDate) => {
                  const { fixedSchedule } = props

                  if (
                    setDate &&
                    setDate instanceof dayjs &&
                    setDate.isValid()
                  ) {
                    props.onStateUpdate({
                      fixedSchedule: {
                        ...fixedSchedule,
                        dateTo: setDate,
                        valid: true
                      }
                    })
                  } else {
                    props.onStateUpdate({
                      fixedSchedule: {
                        ...fixedSchedule,
                        valid: false,
                        dateTo: fixedSchedule.dateTo
                      }
                    })
                  }
                }}
              />
            </section>
            <section className="ActionScheduleChoice__set-date-input-container">
              <SelectTimezone
                value={props.timezone}
                guess
                variant="light"
                onChange={(value) => {
                  props.onStateUpdate({ timezone: value || undefined })
                }}
              />
            </section>
            <ButtonPrimary
              isFullWidth
              onClick={() => {
                props.onStateUpdate({
                  scheduleType: CampaignScheduleEnum.FIXED,
                  relativeSchedule: {
                    dayFrom: null,
                    dayTo: null,
                    timeFrom: null,
                    timeTo: null
                  }
                })
                props.onSave()
              }}
              isLoading={isFixedScheduleSaving()}
              disabled={!isFixedChoiceValid() || isRelativeScheduleSaving()}
            >
              <Trans>Confirm schedule and save</Trans>
            </ButtonPrimary>
          </ActionScheduleChoice>
        )}
      </div>
    </div>
  )
}
